import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getCommerceContext, getCommerceUsers, wizardSaveStep, wizardStartSession } from '../../services/purchaseProgramApi';

// ─── Step definitions ─────────────────────────────────────────────────────────

const STEPS = [
  {
    id: 'commerce_site',
    question: 'Which ExtremeB2B storefront site does this program apply to?',
    hint: 'Choose the ecommerce site (site UUID). This is different from a checkout ship-to / delivery address.',
    type: 'commerce_scope',
    scopePart: 'site',
    required: true,
  },
  {
    id: 'commerce_customers',
    question: 'Which customer accounts should this program include?',
    hint: 'Pick specific Customer Identity accounts on this site, or leave none selected to include every account on the site.',
    type: 'commerce_scope',
    scopePart: 'customers',
    showWhen: (a) => !!a.customer_site?.selectedSite?.id,
    required: false,
  },
  {
    id: 'commerce_basis',
    question: 'Is this a user-level program or a shipping-location-level program?',
    hint: 'User-level limits which enrolled users see the program. Shipping-location-level limits which checkout ship-to addresses qualify.',
    type: 'commerce_scope',
    scopePart: 'basis',
    showWhen: (a) => !!a.customer_site?.selectedSite?.id,
    required: true,
  },
  {
    id: 'commerce_audience_users',
    question: 'Which users can access this program?',
    hint: 'All enrolled users under the selected customers, or only specific users you pick.',
    type: 'commerce_scope',
    scopePart: 'audience_users',
    showWhen: (a) => a.customer_site?.restriction_basis === 'users',
    required: false,
  },
  {
    id: 'commerce_audience_delivery',
    question: 'Which shipping addresses qualify at checkout?',
    hint: 'All saved ship-to addresses for the selected customers, or only selected address IDs.',
    type: 'commerce_scope',
    scopePart: 'audience_delivery',
    showWhen: (a) => a.customer_site?.restriction_basis === 'delivery',
    required: false,
  },
  {
    id: 'name',
    question: 'What would you like to call this program?',
    hint: 'A short, descriptive name shown to users and admins.',
    type: 'text',
    placeholder: 'e.g. PPE Monthly Allowance, Uniform Budget',
    required: true,
  },
  {
    id: 'description',
    question: 'Briefly describe the purpose of this program.',
    hint: "Shown to users. Helps admins understand the program's intent.",
    type: 'textarea',
    placeholder: 'e.g. Each employee can purchase one pair of gloves and boots per month.',
    required: true,
  },
  {
    id: 'product_list_setup',
    question: 'Which product lists can users purchase from?',
    hint: 'Select all lists that apply. Set a max quantity per list per period (e.g. 1 pair of gloves).',
    type: 'product_list_picker',
    required: false,
  },
  {
    id: 'period_type',
    question: 'How often should the allowance reset?',
    hint: "Controls when each user's selections refresh.",
    type: 'options',
    options: [
      { label: 'Never (one-time purchase)', value: 'none', icon: 'bi-infinity', color: '#6366f1' },
      { label: 'Every N days (org-wide)', value: 'days', icon: 'bi-calendar-range', color: '#0891b2' },
      { label: 'Per user hire date (annual)', value: 'hire_date', icon: 'bi-person-badge', color: '#7c3aed' },
    ],
  },
  {
    id: 'period_interval_days',
    question: 'How many days between each reset?',
    hint: 'Common: 30 (monthly), 90 (quarterly), 365 (annual).',
    type: 'number_or_skip',
    placeholder: 'e.g. 30',
    options: ['30 days (monthly)', '90 days (quarterly)', '180 days (semi-annual)', '365 days (annual)'],
    showWhen: (a) => a.period_type === 'days',
  },
  {
    id: 'dates',
    question: 'When does this program run?',
    hint: 'Start date is required. Leave end date blank for an ongoing program.',
    type: 'date_range',
  },
  {
    id: 'limit_type',
    question: 'What type of program-level limit applies?',
    hint: 'Per-list limits set above always take priority. This is a fallback for lists without a specific limit.',
    type: 'options',
    options: [
      { label: 'Quantity limit (items per period)', value: 'qty', icon: 'bi-123', color: '#0891b2' },
      { label: 'Dollar limit (spend per period)', value: 'dollar', icon: 'bi-currency-dollar', color: '#7c3aed' },
      { label: 'Both qty and dollar limits', value: 'both', icon: 'bi-layers', color: '#d97706' },
      { label: 'No program-level limit', value: 'none', icon: 'bi-dash-circle', color: '#6b7280' },
    ],
    required: false,
  },
  {
    id: 'item_limit',
    question: 'How many items can a user purchase per period (program-wide)?',
    hint: 'This is the fallback quantity limit for lists without a per-list qty cap.',
    type: 'number_or_skip',
    placeholder: 'e.g. 5',
    options: ['1', '2', '3', '5', '10', 'No limit'],
    showWhen: (a) => a.limit_type === 'qty' || a.limit_type === 'both',
    required: false,
  },
  {
    id: 'amount_limit',
    question: 'What is the maximum dollar spend per period (program-wide)?',
    hint: 'This is the fallback dollar limit for lists without a per-list dollar cap.',
    type: 'number_or_skip',
    placeholder: 'Amount in $',
    options: ['$100', '$250', '$500', '$1,000', '$2,500', 'No limit'],
    showWhen: (a) => a.limit_type === 'dollar' || a.limit_type === 'both',
    required: false,
  },
  {
    id: 'is_active',
    question: 'Should this program be active immediately?',
    hint: 'Inactive programs are saved but not visible to users.',
    type: 'options',
    options: [
      { label: 'Yes, activate now', value: true, icon: 'bi-lightning-charge-fill', color: '#16a34a' },
      { label: 'No, save as draft', value: false, icon: 'bi-pencil-square', color: '#d97706' },
    ],
  },
];

const TOTAL_STEPS = STEPS.filter((s) => !s.showWhen).length + 1; // +1 for summary

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseAmountOption(label) {
  if (label === 'No limit') return null;
  return Number(label.replace(/[^0-9.]/g, ''));
}

function parseQtyOption(label) {
  if (label === 'No limit') return null;
  return Number(label.replace(/[^0-9]/g, ''));
}

function parseDaysOption(label) {
  const m = label.match(/^(\d+)/);
  return m ? Number(m[1]) : null;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function OptionButton({ option, selected, onClick }) {
  const isObj = typeof option === 'object' && option !== null;
  const label = isObj ? option.label : option;
  const icon = isObj ? option.icon : null;
  const color = isObj ? option.color : '#1a56db';

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 18px', borderRadius: 10, cursor: 'pointer',
        border: `2px solid ${selected ? color : '#e5e7eb'}`,
        background: selected ? `${color}14` : '#fff',
        color: selected ? color : '#374151',
        fontWeight: selected ? 700 : 400,
        fontSize: 14, transition: 'all .15s', textAlign: 'left',
        boxShadow: selected ? `0 0 0 3px ${color}22` : 'none',
      }}
    >
      {icon && <i className={`bi ${icon}`} style={{ fontSize: 18, color }} aria-hidden="true" />}
      {label}
      {selected && <i className="bi bi-check-circle-fill ms-auto" style={{ color, fontSize: 16 }} aria-hidden="true" />}
    </button>
  );
}

function SummaryRow({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{ display: 'flex', gap: 12, padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
      <span style={{ fontSize: 13, color: '#6b7280', width: 160, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{String(value)}</span>
    </div>
  );
}

// ─── Product list picker step ─────────────────────────────────────────────────
function ProductListPickerStep({ productLists, selections, onChange }) {
  const isSelected = (id) => selections.some(s => s.list_id === id);

  const toggle = (pl) => {
    if (isSelected(pl.id)) {
      onChange(selections.filter(s => s.list_id !== pl.id));
    } else {
      onChange([...selections, { list_id: pl.id, list_name: pl.list_name, max_qty: null, max_amount: null }]);
    }
  };

  const update = (id, field, val) => {
    const num = val === '' ? null : Number(val);
    onChange(selections.map(s => s.list_id === id ? { ...s, [field]: isNaN(num) ? null : num } : s));
  };

  if (!productLists || productLists.length === 0) {
    return (
      <div style={{ background: '#f9fafb', border: '1px dashed #d1d5db', borderRadius: 10, padding: '18px 20px', fontSize: 13, color: '#6b7280', textAlign: 'center' }}>
        <i className="bi bi-box-seam" style={{ fontSize: 28, display: 'block', marginBottom: 8, color: '#d1d5db' }} />
        No product lists found. Create lists in the <strong>Product Lists</strong> section first, then come back to set up a program.
        <br />
        <span style={{ fontSize: 12, color: '#9ca3af', marginTop: 6, display: 'block' }}>You can also skip this step and assign lists after creation.</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Hierarchy tip */}
      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#1e40af', marginBottom: 4 }}>
        <i className="bi bi-info-circle-fill me-1" />
        <strong>Limit hierarchy:</strong> Per-list limits override program-level limits. Leave blank to inherit the program limit.
      </div>

      {productLists.map(pl => {
        const sel = selections.find(s => s.list_id === pl.id);
        const active = !!sel;
        const hasQty = active && sel.max_qty != null && sel.max_qty !== '';
        const hasAmt = active && sel.max_amount != null && sel.max_amount !== '';
        return (
          <div key={pl.id} style={{
            border: `2px solid ${active ? '#1a56db' : '#e5e7eb'}`,
            borderRadius: 12, padding: '12px 16px', background: active ? '#eff6ff' : '#fff',
            transition: 'all .15s',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input type="checkbox" id={`wpl-${pl.id}`} checked={active} onChange={() => toggle(pl)}
                style={{ width: 18, height: 18, accentColor: '#1a56db', cursor: 'pointer', flexShrink: 0 }} />
              <label htmlFor={`wpl-${pl.id}`} style={{ flex: 1, cursor: 'pointer', margin: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{pl.list_name}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  <i className="bi bi-box-seam me-1" />{pl.item_count || 0} products in this list
                </div>
              </label>
            </div>

            {active && (
              <div style={{ marginTop: 10, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {/* Qty limit */}
                <div style={{ flex: 1, minWidth: 140 }}>
                  <label style={{ fontSize: 12, color: '#374151', fontWeight: 600, display: 'block', marginBottom: 3 }}>
                    <i className="bi bi-123 me-1" />Qty limit (items)
                  </label>
                  <input type="number" min="1" placeholder="Inherit from program"
                    value={sel.max_qty == null ? '' : sel.max_qty}
                    onChange={e => update(pl.id, 'max_qty', e.target.value)}
                    className="form-control form-control-sm"
                    style={{ borderColor: hasQty ? '#1a56db' : '#d1d5db' }}
                    aria-label={`Max quantity for ${pl.list_name}`} />
                </div>
                {/* Dollar limit */}
                <div style={{ flex: 1, minWidth: 140 }}>
                  <label style={{ fontSize: 12, color: '#374151', fontWeight: 600, display: 'block', marginBottom: 3 }}>
                    <i className="bi bi-currency-dollar me-1" />Dollar limit ($)
                  </label>
                  <input type="number" min="0" step="0.01" placeholder="Inherit from program"
                    value={sel.max_amount == null ? '' : sel.max_amount}
                    onChange={e => update(pl.id, 'max_amount', e.target.value)}
                    className="form-control form-control-sm"
                    style={{ borderColor: hasAmt ? '#1a56db' : '#d1d5db' }}
                    aria-label={`Dollar limit for ${pl.list_name}`} />
                </div>
              </div>
            )}

            {active && (
              <div style={{ marginTop: 8, padding: '6px 10px', background: '#dbeafe', borderRadius: 6, fontSize: 12, color: '#1e40af' }}>
                <i className="bi bi-check-circle-fill me-1" />
                {[
                  hasQty && `up to ${sel.max_qty} item${sel.max_qty !== 1 ? 's' : ''}`,
                  hasAmt && `up to $${Number(sel.max_amount).toFixed(2)}`,
                ].filter(Boolean).join(' and ') || 'Uses program-level limits'} from <strong>{pl.list_name}</strong> per period.
              </div>
            )}
          </div>
        );
      })}
      {selections.length === 0 && (
        <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
          <i className="bi bi-info-circle me-1" />
          No lists selected — users will see the full product catalog. Select at least one list to restrict products.
        </p>
      )}
    </div>
  );
}

// ─── Commerce scope (split wizard steps) — live CI data ─────────────────────
function CommerceScopeStepBody({ scopePart, value, onChange, initialScoping = {} }) {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [loadingCust, setLoadingCust] = useState(false);
  const [search, setSearch] = useState('');
  const [siteSearch, setSiteSearch] = useState('');
  const [custUsersMap, setCustUsersMap] = useState({});
  const [userPickSearch, setUserPickSearch] = useState('');
  const [deliveryPickSearch, setDeliveryPickSearch] = useState('');

  const valueRef = useRef(value);
  valueRef.current = value;

  const selectedSite = value.selectedSite || initialScoping.selectedSite || null;
  const selectedCustomers = (value.selectedCustomers?.length ? value.selectedCustomers : null)
    || initialScoping.selectedCustomers
    || [];
  const userScopeMode = value.user_scope_mode === 'select' ? 'select' : 'all';
  const deliveryScopeMode = value.delivery_scope_mode === 'select' ? 'select' : 'all';
  const selectedUsersPick = Array.isArray(value.selected_users) ? value.selected_users : [];

  const filteredSites = useMemo(() => {
    const q = siteSearch.trim().toLowerCase();
    if (!q) return sites;
    return sites.filter(s =>
      (s.name || '').toLowerCase().includes(q)
      || (s.url || '').toLowerCase().includes(q)
      || String(s.id || '').includes(q),
    );
  }, [sites, siteSearch]);

  const filtered = useMemo(() => customers.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(q)
      || (c.erp_customer_id || '').toLowerCase().includes(q)
      || (c.email || '').toLowerCase().includes(q);
  }), [customers, search]);

  const usersForPicker = useMemo(() => {
    const rows = [];
    selectedCustomers.forEach(c => {
      const pack = custUsersMap[c.id];
      (pack?.users || []).forEach(u => {
        const displayName = u.display_name
          || [u.first_name, u.last_name].filter(Boolean).join(' ')
          || u.email
          || '';
        rows.push({
          id: u.id,
          customer_id: c.id,
          customer_name: c.name,
          display_name: displayName,
          email: u.email || '',
        });
      });
    });
    rows.sort((a, b) => (a.display_name || '').localeCompare(b.display_name || '', undefined, { sensitivity: 'base' }));
    return rows;
  }, [selectedCustomers, custUsersMap]);

  const filteredUsersPick = useMemo(() => {
    const q = userPickSearch.trim().toLowerCase();
    if (!q) return usersForPicker;
    return usersForPicker.filter(u =>
      (u.display_name || '').toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q)
      || (u.customer_name || '').toLowerCase().includes(q),
    );
  }, [usersForPicker, userPickSearch]);

  const addressesForPicker = useMemo(() => {
    const rows = [];
    selectedCustomers.forEach((c) => {
      const pack = custUsersMap[c.id];
      const cust = customers.find((x) => x.id === c.id) || c;
      (pack?.addresses || []).forEach((a) => {
        const line = [a.city, a.state_province].filter(Boolean).join(', ');
        rows.push({
          id: a.id,
          customer_id: c.id,
          customer_name: cust.name,
          label: a.label || 'Address',
          summary: `${line}${a.postal_code ? ` ${a.postal_code}` : ''}`.trim(),
        });
      });
    });
    rows.sort((a, b) => (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' }));
    return rows;
  }, [selectedCustomers, custUsersMap, customers]);

  const filteredAddrPick = useMemo(() => {
    const q = deliveryPickSearch.trim().toLowerCase();
    if (!q) return addressesForPicker;
    return addressesForPicker.filter(
      (a) =>
        (a.label || '').toLowerCase().includes(q)
        || (a.summary || '').toLowerCase().includes(q)
        || (a.customer_name || '').toLowerCase().includes(q),
    );
  }, [addressesForPicker, deliveryPickSearch]);

  const selectedDeliveryIds = useMemo(
    () => String(value.delivery_ship_to_csv || '').split(',').map((x) => x.trim()).filter(Boolean),
    [value.delivery_ship_to_csv],
  );

  useEffect(() => {
    const snap = valueRef.current;
    const prev = Array.isArray(snap.selected_users) ? snap.selected_users : [];
    const next = prev.filter((u) => {
      if (!u.customer_id) return true;
      return selectedCustomers.some((c) => c.id === u.customer_id);
    });
    if (next.length !== prev.length) {
      onChange({ ...snap, selected_users: next });
    }
  }, [selectedCustomers]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (userScopeMode !== 'select' || selectedCustomers.length === 0) return;
    selectedCustomers.forEach(c => {
      if (!custUsersMap[c.id]) {
        getCommerceUsers(c.id).then(data => {
          setCustUsersMap(u => ({ ...u, [c.id]: data }));
        }).catch(() => {});
      }
    });
  }, [userScopeMode, selectedCustomers]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (value.restriction_basis !== 'delivery' || deliveryScopeMode !== 'select' || selectedCustomers.length === 0) return;
    selectedCustomers.forEach((c) => {
      if (!custUsersMap[c.id]) {
        getCommerceUsers(c.id).then((data) => {
          setCustUsersMap((u) => ({ ...u, [c.id]: data }));
        }).catch(() => {});
      }
    });
  }, [deliveryScopeMode, selectedCustomers, value.restriction_basis]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getCommerceContext().then(data => {
      const ordered = [...(data?.sites || [])].sort((a, b) => {
        const aOn = a.is_active !== false;
        const bOn = b.is_active !== false;
        if (aOn !== bOn) return aOn ? -1 : 1;
        return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
      });
      setSites(ordered);
      const snap = valueRef.current;
      const initSite = initialScoping.selectedSite;
      if (initSite && !snap.selectedSite) {
        onChange({
          ...snap,
          selectedSite: initSite,
          ship_to_csv: snap.ship_to_csv || String(initSite.id),
          customer_csv: snap.customer_csv || initialScoping.customer_csv || '',
          selectedCustomers: snap.selectedCustomers?.length ? snap.selectedCustomers : (initialScoping.selectedCustomers || []),
        });
        loadCusts(initSite.id);
      } else {
        const shipCsv = String(snap.ship_to_csv || '').trim();
        const siteIdFromCsv = shipCsv.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean)[0];
        if (siteIdFromCsv && !snap.selectedSite) {
          const found = ordered.find((s) => s.id === siteIdFromCsv);
          const siteObj = found || { id: siteIdFromCsv, name: 'Storefront site', is_active: true };
          onChange({
            ...snap,
            selectedSite: siteObj,
            ship_to_csv: siteIdFromCsv,
          });
          loadCusts(siteIdFromCsv);
        } else if ((snap.selectedSite || initialScoping.selectedSite)?.id) {
          loadCusts((snap.selectedSite || initialScoping.selectedSite).id);
        } else if (ordered.length === 1 && !siteIdFromCsv) {
          const only = ordered[0];
          onChange({ ...snap, selectedSite: only, ship_to_csv: only.id });
          loadCusts(only.id);
        }
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCusts = (siteId) => {
    setLoadingCust(true);
    getCommerceContext(siteId).then(data => {
      const cList = data?.customers || [];
      setCustomers(cList);
      const snap = valueRef.current;
      const csvIds = String(snap.customer_csv || '').split(',').map((x) => x.trim()).filter(Boolean);
      let nextSelected = snap.selectedCustomers || [];
      if (!nextSelected.length && csvIds.length > 0) {
        nextSelected = csvIds.map((id) => cList.find((c) => c.id === id)).filter(Boolean);
      }
      const patch = {};
      if (nextSelected.length > 0 && !(snap.selectedCustomers || []).length) {
        patch.selectedCustomers = nextSelected;
        patch.customer_csv = nextSelected.map((c) => c.id).join(', ');
      } else if (cList.length === 1 && !csvIds.length && !(snap.selectedCustomers || []).length) {
        patch.selectedCustomers = [cList[0]];
        patch.customer_csv = cList[0].id;
      }
      if (Object.keys(patch).length > 0) {
        onChange({ ...snap, ...patch });
      }
    }).catch(() => {}).finally(() => setLoadingCust(false));
  };

  const handleSiteClick = (site) => {
    setSiteSearch('');
    setCustUsersMap({});
    setUserPickSearch('');
    setDeliveryPickSearch('');
    onChange({
      ...value,
      selectedSite: site,
      ship_to_csv: site.id,
      selectedCustomers: [],
      customer_csv: '',
      user_scope_mode: 'all',
      selected_users: [],
      restriction_basis: undefined,
      delivery_scope_mode: 'all',
      delivery_ship_to_csv: '',
    });
    loadCusts(site.id);
  };

  const toggleCust = (cust) => {
    const prev = value.selectedCustomers?.length
      ? value.selectedCustomers
      : (initialScoping.selectedCustomers || []);
    const exists = prev.some(c => c.id === cust.id);
    const next = exists ? prev.filter(c => c.id !== cust.id) : [...prev, cust];
    if (!exists && !custUsersMap[cust.id]) {
      getCommerceUsers(cust.id).then(data => {
        setCustUsersMap(m => ({ ...m, [cust.id]: data }));
      }).catch(() => {});
    }
    onChange({ ...value, selectedCustomers: next, customer_csv: next.map(c => c.id).join(', ') });
  };

  const selectAllFiltered = () => {
    const base = value.selectedCustomers?.length
      ? value.selectedCustomers
      : (initialScoping.selectedCustomers || []);
    const ids = new Set(base.map(c => c.id));
    const next = [...base];
    filtered.forEach(c => {
      if (!ids.has(c.id)) {
        ids.add(c.id);
        next.push(c);
      }
    });
    onChange({ ...value, selectedCustomers: next, customer_csv: next.map(c => c.id).join(', ') });
  };

  const clearCustomers = () => {
    onChange({
      ...value,
      selectedCustomers: [],
      customer_csv: '',
      user_scope_mode: 'all',
      selected_users: [],
      delivery_scope_mode: 'all',
      delivery_ship_to_csv: '',
    });
    setUserPickSearch('');
    setDeliveryPickSearch('');
  };

  const togglePickUser = (row) => {
    const prev = Array.isArray(value.selected_users) ? value.selected_users : [];
    const exists = prev.some(p => p.id === row.id);
    const next = exists ? prev.filter(p => p.id !== row.id) : [...prev, { id: row.id, customer_id: row.customer_id }];
    onChange({ ...value, selected_users: next });
  };

  const selectAllUsersFiltered = () => {
    const ids = new Set((value.selected_users || []).map(u => u.id));
    const add = filteredUsersPick.filter(u => !ids.has(u.id)).map(u => ({ id: u.id, customer_id: u.customer_id }));
    onChange({ ...value, selected_users: [...(value.selected_users || []), ...add] });
  };

  const clearUserPick = () => onChange({ ...value, selected_users: [] });

  const togglePickAddr = (row) => {
    const id = String(row.id);
    const picked = new Set(selectedDeliveryIds);
    if (picked.has(id)) picked.delete(id);
    else picked.add(id);
    onChange({ ...value, delivery_ship_to_csv: [...picked].join(', ') });
  };

  const selectAllAddrFiltered = () => {
    const picked = new Set(selectedDeliveryIds);
    filteredAddrPick.forEach((row) => picked.add(String(row.id)));
    onChange({ ...value, delivery_ship_to_csv: [...picked].join(', ') });
  };

  const clearAddrPick = () => onChange({ ...value, delivery_ship_to_csv: '' });

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border spinner-border-sm text-primary" role="status" aria-label="Loading" />
      </div>
    );
  }

  const siteBlock = (
    <div className="mb-1">
      <label className="form-label small text-muted mb-1" htmlFor="wiz-site-search">Find a site</label>
      <div className="input-group input-group-sm mb-2">
        <span className="input-group-text bg-white"><i className="bi bi-search" aria-hidden="true" /></span>
        <input
          id="wiz-site-search"
          className="form-control"
          placeholder="Name, URL, or ID…"
          value={siteSearch}
          onChange={(e) => setSiteSearch(e.target.value)}
          autoComplete="off"
        />
      </div>
      {sites.length === 0 && (
        <p className="small text-muted mb-0">No sites found. Create a site in Customer Identity.</p>
      )}
      <div className="row g-2">
        {filteredSites.map((s) => {
          const active = selectedSite?.id === s.id;
          const siteOn = s.is_active !== false;
          return (
            <div key={s.id} className="col-12 col-md-6">
              <button
                type="button"
                className={`w-100 text-start rounded-3 border-2 py-2 px-3 small ${active ? 'border-primary bg-primary-subtle' : 'border-light bg-white'}`}
                style={{ opacity: siteOn ? 1 : 0.85 }}
                onClick={() => handleSiteClick(s)}
              >
                <div className="fw-semibold text-truncate">
                  <i className={`bi ${active ? 'bi-check-circle-fill text-primary' : 'bi-globe text-secondary'} me-1`} aria-hidden="true" />
                  {s.name}
                  {!siteOn && <span className="badge bg-secondary-subtle text-secondary ms-1 text-uppercase" style={{ fontSize: 9 }}>inactive</span>}
                </div>
                <code className="text-muted" style={{ fontSize: 10 }}>{s.id}</code>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  const customersBlock = selectedSite ? (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <span className="small fw-bold text-secondary text-uppercase" style={{ letterSpacing: '.04em' }}>
          <i className="bi bi-building me-1" aria-hidden="true" />
          Customers · {selectedSite.name}
        </span>
        <span className="badge rounded-pill bg-primary-subtle text-primary border border-primary-subtle">
          {selectedCustomers.length} selected
        </span>
      </div>
      {loadingCust ? (
        <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary" role="status" /></div>
      ) : (
        <>
          <label className="form-label small text-muted mb-1" htmlFor="wiz-cust-search">Search customers</label>
          <input
            id="wiz-cust-search"
            className="form-control form-control-sm mb-2"
            placeholder="Name, email, ERP ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="d-flex flex-wrap gap-2 mb-2">
            <div className="btn-group btn-group-sm">
              <button type="button" className="btn btn-outline-primary" onClick={selectAllFiltered} disabled={filtered.length === 0}>
                Select all ({filtered.length})
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={clearCustomers} disabled={selectedCustomers.length === 0}>
                Clear
              </button>
            </div>
          </div>
          {filtered.length === 0 && <p className="small text-muted">No customers match.</p>}
          <div className="rounded-3 border bg-white" style={{ maxHeight: 280, overflowY: 'auto' }}>
            {filtered.map((c) => {
              const active = selectedCustomers.some((x) => x.id === c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  className="w-100 border-0 border-bottom bg-transparent text-start px-3 py-2 d-flex align-items-center gap-2"
                  style={{ background: active ? '#eff6ff' : '#fff' }}
                  onClick={() => toggleCust(c)}
                >
                  <i className={`bi ${active ? 'bi-check-circle-fill text-primary' : 'bi-circle text-secondary'}`} style={{ fontSize: 16 }} aria-hidden="true" />
                  <div className="flex-grow-1 min-w-0">
                    <div className="fw-semibold small">{c.name}</div>
                    <div className="text-muted" style={{ fontSize: 11 }}>
                      {c.erp_customer_id && `ERP ${c.erp_customer_id} · `}
                      {c.email}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  ) : (
    <p className="small text-muted mb-0">
      <i className="bi bi-info-circle me-1" aria-hidden="true" />Select a site in the previous step first.
    </p>
  );

  const basisBlock = (
    <div className="row g-2">
      <div className="col-12 col-md-6">
        <button
          type="button"
          className={`btn w-100 py-3 ${value.restriction_basis === 'delivery' ? 'btn-outline-primary' : 'btn-primary'}`}
          onClick={() => onChange({
            ...value,
            restriction_basis: 'users',
            delivery_scope_mode: 'all',
            delivery_ship_to_csv: '',
          })}
        >
          <i className="bi bi-people-fill d-block mb-1 fs-5" aria-hidden="true" />
          User-level program
        </button>
      </div>
      <div className="col-12 col-md-6">
        <button
          type="button"
          className={`btn w-100 py-3 ${value.restriction_basis === 'delivery' ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => onChange({
            ...value,
            restriction_basis: 'delivery',
            user_scope_mode: 'all',
            selected_users: [],
          })}
        >
          <i className="bi bi-truck d-block mb-1 fs-5" aria-hidden="true" />
          Shipping-location-level program
        </button>
      </div>
      <p className="small text-muted mb-0 mt-2">
        User-level narrows which enrolled users see the program. Shipping-location-level narrows which checkout ship-to addresses qualify (Customer Identity addresses).
      </p>
    </div>
  );

  const audienceUsersBlock = (
    <div className="card border-info border-opacity-50">
      <div className="card-body py-3">
        <div className="fw-semibold small mb-2">
          <i className="bi bi-person-badge text-primary me-1" aria-hidden="true" />
          Which users can use this program?
        </div>
        <div className="btn-group w-100 mb-2" role="group">
          <button
            type="button"
            className={`btn btn-sm ${userScopeMode === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => onChange({ ...value, user_scope_mode: 'all', selected_users: [] })}
          >
            All enrolled users
          </button>
          <button
            type="button"
            className={`btn btn-sm ${userScopeMode === 'select' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => onChange({ ...value, user_scope_mode: 'select' })}
          >
            Only selected users
          </button>
        </div>
        {userScopeMode === 'select' && (
          <>
            <input
              className="form-control form-control-sm mb-2"
              placeholder="Search users…"
              value={userPickSearch}
              onChange={(e) => setUserPickSearch(e.target.value)}
            />
            <div className="d-flex flex-wrap gap-2 mb-2">
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={selectAllUsersFiltered} disabled={filteredUsersPick.length === 0}>
                Select all ({filteredUsersPick.length})
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearUserPick} disabled={selectedUsersPick.length === 0}>
                Clear
              </button>
              <span className="small text-muted align-self-center">{selectedUsersPick.length} selected</span>
            </div>
            <div className="rounded-3 border bg-white" style={{ maxHeight: 220, overflowY: 'auto' }}>
              {filteredUsersPick.map((row) => {
                const on = selectedUsersPick.some((u) => u.id === row.id);
                return (
                  <button
                    key={`${row.customer_id}-${row.id}`}
                    type="button"
                    className="w-100 border-0 border-bottom bg-transparent text-start px-2 py-2 d-flex align-items-center gap-2"
                    style={{ background: on ? '#eff6ff' : '#fff' }}
                    onClick={() => togglePickUser(row)}
                  >
                    <i className={`bi ${on ? 'bi-check-circle-fill text-primary' : 'bi-circle text-secondary'}`} aria-hidden="true" />
                    <div className="flex-grow-1 min-w-0">
                      <div className="small fw-semibold text-truncate">{row.display_name}</div>
                      <div className="text-muted text-truncate" style={{ fontSize: 10 }}>
                        {row.email && `${row.email} · `}
                        <span className="badge bg-light text-secondary border">{row.customer_name}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
              {filteredUsersPick.length === 0 && (
                <div className="small text-muted text-center py-3">Select customers in step 2 to load users.</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );

  const audienceDeliveryBlock = (
    <div className="card border-info border-opacity-50">
      <div className="card-body py-3">
        <div className="fw-semibold small mb-2">
          <i className="bi bi-truck text-primary me-1" aria-hidden="true" />
          Which shipping addresses qualify at checkout?
        </div>
        <div className="btn-group w-100 mb-2" role="group">
          <button
            type="button"
            className={`btn btn-sm ${deliveryScopeMode === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => onChange({ ...value, delivery_scope_mode: 'all', delivery_ship_to_csv: '' })}
          >
            All saved addresses
          </button>
          <button
            type="button"
            className={`btn btn-sm ${deliveryScopeMode === 'select' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => onChange({ ...value, delivery_scope_mode: 'select' })}
          >
            Only selected addresses
          </button>
        </div>
        {deliveryScopeMode === 'select' && (
          <>
            <input
              className="form-control form-control-sm mb-2"
              placeholder="Search addresses…"
              value={deliveryPickSearch}
              onChange={(e) => setDeliveryPickSearch(e.target.value)}
            />
            <div className="d-flex flex-wrap gap-2 mb-2">
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={selectAllAddrFiltered} disabled={filteredAddrPick.length === 0}>
                Select all ({filteredAddrPick.length})
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearAddrPick} disabled={selectedDeliveryIds.length === 0}>
                Clear
              </button>
              <span className="small text-muted align-self-center">{selectedDeliveryIds.length} selected</span>
            </div>
            <div className="rounded-3 border bg-white" style={{ maxHeight: 220, overflowY: 'auto' }}>
              {filteredAddrPick.map((row) => {
                const on = selectedDeliveryIds.includes(String(row.id));
                return (
                  <button
                    key={`${row.customer_id}-${row.id}`}
                    type="button"
                    className="w-100 border-0 border-bottom bg-transparent text-start px-2 py-2 d-flex align-items-center gap-2"
                    style={{ background: on ? '#eff6ff' : '#fff' }}
                    onClick={() => togglePickAddr(row)}
                  >
                    <i className={`bi ${on ? 'bi-check-circle-fill text-primary' : 'bi-circle text-secondary'}`} aria-hidden="true" />
                    <div className="flex-grow-1 min-w-0">
                      <div className="small fw-semibold text-truncate">{row.label}</div>
                      <div className="text-muted text-truncate" style={{ fontSize: 10 }}>
                        {row.summary} · <span className="badge bg-light text-secondary border">{row.customer_name}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
              {filteredAddrPick.length === 0 && (
                <div className="small text-muted text-center py-3">Select customers in step 2 with saved addresses.</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );

  let body = null;
  if (scopePart === 'site') body = siteBlock;
  else if (scopePart === 'customers') body = customersBlock;
  else if (scopePart === 'basis') body = basisBlock;
  else if (scopePart === 'audience_users') body = audienceUsersBlock;
  else if (scopePart === 'audience_delivery') body = audienceDeliveryBlock;

  const recap = (scopePart === 'audience_users' || scopePart === 'audience_delivery') && selectedSite && (
    <div className="mt-3 p-2 rounded-3 border border-success-subtle bg-success-subtle small text-success-emphasis">
      <i className="bi bi-check-circle-fill me-1" aria-hidden="true" />
      {selectedSite.name} · {selectedCustomers.length} customer{selectedCustomers.length !== 1 ? 's' : ''}
      {value.restriction_basis === 'users' && userScopeMode === 'select' && selectedUsersPick.length > 0 && (
        <span className="d-block mt-1 text-dark">{selectedUsersPick.length} user{selectedUsersPick.length !== 1 ? 's' : ''} allowed</span>
      )}
      {value.restriction_basis === 'delivery' && deliveryScopeMode === 'select' && selectedDeliveryIds.length > 0 && (
        <span className="d-block mt-1 text-dark">{selectedDeliveryIds.length} ship-to address{selectedDeliveryIds.length !== 1 ? 'es' : ''} allowed</span>
      )}
    </div>
  );

  return (
    <div className="container-fluid px-0">
      {body}
      {recap}
    </div>
  );
}

function customerSiteSeedFromScoping(initialScoping = {}) {
  if (!initialScoping || typeof initialScoping !== 'object') return null;
  const ship = String(initialScoping.ship_to_csv || '').trim();
  const cust = String(initialScoping.customer_csv || '').trim();
  const siteObj = initialScoping.selectedSite || null;
  const custObjs = Array.isArray(initialScoping.selectedCustomers) ? initialScoping.selectedCustomers : [];
  if (!ship && !cust && !siteObj && custObjs.length === 0) return null;
  const customer_csv = cust || custObjs.map((c) => c.id).filter(Boolean).join(', ');
  const ship_to_csv = ship || (siteObj?.id ? String(siteObj.id) : '');
  const userScopeModeSeed = initialScoping.userScopeMode === 'select' ? 'select' : 'all';
  const selectedUsersSeed = Array.isArray(initialScoping.selectedUsers) ? initialScoping.selectedUsers : [];
  const restrictionBasisSeed = initialScoping.restriction_basis === 'delivery' ? 'delivery' : 'users';
  const deliveryCsv = String(initialScoping.delivery_ship_to_csv || '').trim();
  const deliveryModeSeed = deliveryCsv ? 'select' : 'all';
  return {
    selectedSite: siteObj,
    selectedCustomers: custObjs,
    ship_to_csv,
    customer_csv,
    user_groups_csv: String(initialScoping.user_groups_csv || '').trim(),
    user_scope_mode: userScopeModeSeed,
    selected_users: selectedUsersSeed,
    restriction_basis: restrictionBasisSeed,
    delivery_scope_mode: restrictionBasisSeed === 'delivery' ? deliveryModeSeed : 'all',
    delivery_ship_to_csv: restrictionBasisSeed === 'delivery' ? deliveryCsv : '',
  };
}

// ─── Main wizard ──────────────────────────────────────────────────────────────

export default function ProgramCreationWizard({ onFinish, onCancel, userId = '', productLists = [], userGroups = [], initialAnswers = {}, initialScoping = {} }) { // eslint-disable-line no-unused-vars
  const [sessionId, setSessionId] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState(() => {
    const base = { ...(initialAnswers || {}) };
    const seed = customerSiteSeedFromScoping(initialScoping);
    base.customer_site = {
      ...(seed || {}),
      ...(base.customer_site || {}),
    };
    return base;
  });
  const [localVal, setLocalVal] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  // Compute visible steps based on current answers
  const visibleSteps = STEPS.filter((s) => !s.showWhen || s.showWhen(answers));
  const isSummary = stepIdx >= visibleSteps.length;
  const currentStep = visibleSteps[stepIdx];
  const progress = Math.round(((stepIdx) / (visibleSteps.length)) * 100);

  // Start or resume wizard session
  useEffect(() => {
    wizardStartSession(userId)
      .then((s) => setSessionId(s?.id))
      .catch((e) => console.error('wizard session', e));
  }, [userId]);

  // Pre-fill localVal from existing answer when step changes
  useEffect(() => {
    if (!currentStep) return;
    const existing = answers[currentStep.id];
    if (currentStep.type === 'text' || currentStep.type === 'textarea' || currentStep.type === 'number_or_skip') {
      setLocalVal(existing != null ? String(existing) : '');
    } else if (currentStep.type === 'date_range') {
      setLocalVal('');
    }
    inputRef.current?.focus();
  }, [stepIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  const setAnswer = useCallback((key, val) => {
    setAnswers((prev) => ({ ...prev, [key]: val }));
  }, []);

  const canAdvance = () => {
    if (isSummary) return true;
    if (!currentStep) return false;
    const cs = answers.customer_site || {};
    if (currentStep.id === 'commerce_site') {
      return !!cs.selectedSite?.id;
    }
    if (currentStep.id === 'commerce_basis') {
      return cs.restriction_basis === 'users' || cs.restriction_basis === 'delivery';
    }
    if (currentStep.id === 'commerce_audience_users') {
      const hasCust = (cs.selectedCustomers || []).length > 0 || String(cs.customer_csv || '').trim();
      if (cs.user_scope_mode === 'select' && hasCust && !(cs.selected_users || []).length) {
        return false;
      }
    }
    if (currentStep.id === 'commerce_audience_delivery') {
      if (cs.delivery_scope_mode === 'select') {
        const ids = String(cs.delivery_ship_to_csv || '').split(',').map((v) => v.trim()).filter(Boolean);
        if (ids.length === 0) return false;
      }
    }
    if (currentStep.required === false) return true;
    const val = answers[currentStep.id];
    if (currentStep.type === 'date_range') return !!answers.start_date;
    return val !== undefined && val !== '' && val !== null;
  };

  const advance = async () => {
    // Flush text/number input value into answers
    if (currentStep?.type === 'text' || currentStep?.type === 'textarea' || currentStep?.type === 'number_or_skip') {
      if (localVal.trim()) setAnswer(currentStep.id, localVal.trim());
    }
    if (isSummary) {
      await doFinish();
    } else {
      const nextIdx = stepIdx + 1;
      // Persist progress
      if (sessionId) {
        wizardSaveStep(sessionId, { step: nextIdx, answers }).catch(() => {});
      }
      setStepIdx(nextIdx);
    }
  };

  const doFinish = async () => {
    setSaving(true);
    const draft = buildDraft();
    if (sessionId) {
      await wizardSaveStep(sessionId, { step: visibleSteps.length, answers: { ...answers, __done__: true, program_draft: draft } }).catch(() => {});
    }
    onFinish(draft);
  };

  const buildDraft = () => {
    const a = answers;
    const listSelections = a.product_list_selections || [];
    const limitType = a.limit_type || 'none';
    // Build per-list limits — include whichever of max_qty / max_amount is set
    const listLimits = listSelections
      .filter(s => (s.max_qty != null && s.max_qty !== '') || (s.max_amount != null && s.max_amount !== ''))
      .map(s => ({
        limit_type: 'list',
        list_id: s.list_id,
        list_name: s.list_name,
        max_qty: (s.max_qty != null && s.max_qty !== '') ? Number(s.max_qty) : null,
        max_amount: (s.max_amount != null && s.max_amount !== '') ? Number(s.max_amount) : null,
      }));
    const wizardPeriod = a.period_type || 'none';
    const apiPeriodType =
      wizardPeriod === 'days'
        ? 'org_interval'
        : wizardPeriod === 'hire_date'
          ? 'user_hire_date'
          : wizardPeriod === 'none'
            ? 'none'
            : wizardPeriod;

    return {
      name: a.name || '',
      description: a.description || '',
      is_active: a.is_active ?? true,
      start_date: a.start_date || null,
      end_date: a.end_date || null,
      // Program-level limits — only set for the chosen limit type
      amount_limit: (limitType === 'dollar' || limitType === 'both') ? (a.amount_limit ?? null) : null,
      item_limit: (limitType === 'qty' || limitType === 'both') ? (a.item_limit ?? null) : null,
      period_type: apiPeriodType,
      period_interval_days: a.period_interval_days ?? null,
      period_anchor_date: a.period_anchor_date || null,
      product_list_ids: listSelections.map(s => s.list_id),
      item_limits: listLimits,
      product_limits_json: JSON.stringify(listLimits),
      assignments: (() => {
        const cs = a.customer_site || {};
        const customerKeys = (cs.customer_csv || '').split(',').map(v => v.trim()).filter(Boolean);
        const shipToKeys   = (cs.ship_to_csv || '').split(',').map(v => v.trim()).filter(Boolean);
        const groupKeys    = (cs.user_groups_csv || '').split(',').map(v => v.trim()).filter(Boolean);
        const basisUsers = cs.restriction_basis !== 'delivery';
        const userRows = (basisUsers && cs.user_scope_mode === 'select' && (cs.selected_users || []).length)
          ? cs.selected_users.map(u => ({ entity_type: 'user', entity_key: String(u.id) }))
          : [];
        const deliveryKeys = (!basisUsers && cs.delivery_scope_mode === 'select')
          ? String(cs.delivery_ship_to_csv || '').split(',').map(v => v.trim()).filter(Boolean)
          : [];
        const deliveryRows = deliveryKeys.map(k => ({ entity_type: 'delivery_ship_to', entity_key: k }));
        return [
          ...(customerKeys.length > 0
            ? customerKeys.map(k => ({ entity_type: 'customer', entity_key: k }))
            : [{ entity_type: 'customer', entity_key: 'self' }]),
          ...shipToKeys.map(k => ({ entity_type: 'ship_to', entity_key: k })),
          ...groupKeys.map(k => ({ entity_type: 'user_group', entity_key: k })),
          ...userRows,
          ...deliveryRows,
        ];
      })(),
    };
  };

  const handleOptionSelect = (stepId, val) => {
    setAnswer(stepId, val);
    // Auto-advance for single-choice steps after a brief delay
    setTimeout(() => {
      setStepIdx((prev) => prev + 1);
    }, 250);
  };

  const handleOptionTextShortcut = (label) => {
    if (currentStep.id === 'amount_limit') {
      const v = parseAmountOption(label);
      setAnswer('amount_limit', v);
      setLocalVal(v != null ? String(v) : '');
    } else if (currentStep.id === 'item_limit') {
      const v = parseQtyOption(label);
      setAnswer('item_limit', v);
      setLocalVal(v != null ? String(v) : '');
    } else if (currentStep.id === 'qty_limit') {
      const v = parseQtyOption(label);
      setAnswer('qty_limit', v);
      setLocalVal(v != null ? String(v) : '');
    } else if (currentStep.id === 'period_interval_days') {
      const v = parseDaysOption(label);
      setAnswer('period_interval_days', v);
      setLocalVal(v != null ? String(v) : '');
    }
    if (label === 'No limit') {
      setTimeout(() => setStepIdx((p) => p + 1), 200);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: 'system-ui, sans-serif' }}>
      {/* Progress bar */}
      <div style={{ padding: '16px 24px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
            {isSummary ? 'Review & Confirm' : `Step ${stepIdx + 1} of ${visibleSteps.length + 1}`}
          </span>
          <span style={{ fontSize: 12, color: '#1a56db', fontWeight: 700 }}>{progress}%</span>
        </div>
        <div style={{ height: 6, background: '#e5e7eb', borderRadius: 99, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg,#1a56db,#6366f1)', borderRadius: 99, transition: 'width .4s ease' }} />
        </div>
      </div>

      {/* Step content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 24px 0' }}>
        {!isSummary && currentStep && (
          <div key={currentStep.id} style={{ animation: 'fadeIn .25s ease' }}>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: '0 0 6px', lineHeight: 1.3 }}>
              {currentStep.question}
            </h2>
            {currentStep.hint && (
              <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 20px' }}>
                <i className="bi bi-info-circle me-1" aria-hidden="true" />{currentStep.hint}
              </p>
            )}

            {/* Options */}
            {currentStep.type === 'options' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {currentStep.options.map((opt) => (
                  <OptionButton
                    key={typeof opt === 'object' ? opt.value : opt}
                    option={opt}
                    selected={answers[currentStep.id] === (typeof opt === 'object' ? opt.value : opt)}
                    onClick={() => handleOptionSelect(currentStep.id, typeof opt === 'object' ? opt.value : opt)}
                  />
                ))}
              </div>
            )}

            {/* Text input */}
            {currentStep.type === 'text' && (
              <input
                ref={inputRef}
                className="form-control form-control-lg"
                value={localVal}
                onChange={(e) => { setLocalVal(e.target.value); setAnswer(currentStep.id, e.target.value); }}
                placeholder={currentStep.placeholder}
                onKeyDown={(e) => e.key === 'Enter' && canAdvance() && advance()}
                style={{ fontSize: 16, borderRadius: 10 }}
              />
            )}

            {/* Textarea */}
            {currentStep.type === 'textarea' && (
              <textarea
                ref={inputRef}
                className="form-control"
                rows={3}
                value={localVal}
                onChange={(e) => { setLocalVal(e.target.value); setAnswer(currentStep.id, e.target.value); }}
                placeholder={currentStep.placeholder}
                style={{ fontSize: 15, borderRadius: 10, resize: 'vertical' }}
              />
            )}

            {/* Number with quick-pick options */}
            {currentStep.type === 'number_or_skip' && (
              <div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                  {currentStep.options.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => handleOptionTextShortcut(opt)}
                      style={{
                        padding: '6px 14px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
                        border: '1.5px solid #d1d5db', background: '#f9fafb',
                        color: '#374151', fontWeight: 500, transition: 'all .12s',
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input
                    ref={inputRef}
                    className="form-control"
                    type="number"
                    min="0"
                    value={localVal}
                    onChange={(e) => {
                      setLocalVal(e.target.value);
                      setAnswer(currentStep.id, e.target.value === '' ? null : Number(e.target.value));
                    }}
                    placeholder={currentStep.placeholder}
                    onKeyDown={(e) => e.key === 'Enter' && advance()}
                    style={{ borderRadius: 10, maxWidth: 200 }}
                  />
                  <span style={{ fontSize: 13, color: '#9ca3af' }}>or leave blank for no limit</span>
                </div>
              </div>
            )}

            {/* Product list picker */}
            {currentStep.type === 'product_list_picker' && (
              <ProductListPickerStep
                productLists={productLists}
                selections={answers.product_list_selections || []}
                onChange={(v) => setAnswer('product_list_selections', v)}
              />
            )}

            {/* Commerce scope — stepped: site → customers → basis → audience */}
            {currentStep.type === 'commerce_scope' && (
              <CommerceScopeStepBody
                scopePart={currentStep.scopePart}
                value={answers.customer_site || {}}
                onChange={(v) => setAnswer('customer_site', v)}
                initialScoping={initialScoping}
              />
            )}

            {/* Date range */}
            {currentStep.type === 'date_range' && (
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {[['start_date', 'Start date *', true], ['end_date', 'End date (optional)', false]].map(([key, label, req]) => (
                  <div key={key} style={{ flex: 1, minWidth: 160 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>{label}</label>
                    <input
                      type="date"
                      className="form-control"
                      value={answers[key] || ''}
                      onChange={(e) => setAnswer(key, e.target.value || null)}
                      required={req}
                      style={{ borderRadius: 10 }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Summary */}
        {isSummary && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: '0 0 4px' }}>
              <i className="bi bi-clipboard-check-fill me-2" style={{ color: '#1a56db' }} aria-hidden="true" />
              Review your program
            </h2>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 20px' }}>
              Double-check everything below then click <strong>{Object.keys(initialAnswers).length > 0 ? 'Save Changes' : 'Create Program'}</strong>.
            </p>
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 20px', marginBottom: 16 }}>
              <SummaryRow label="Program name" value={answers.name} />
              <SummaryRow label="Description" value={answers.description} />
              <SummaryRow label="Active immediately" value={answers.is_active === true ? 'Yes' : 'No — draft'} />
              <SummaryRow label="Start date" value={answers.start_date} />
              <SummaryRow label="End date" value={answers.end_date || 'No end date'} />
              <SummaryRow label="Reset period" value={{ none: 'Never (one-time)', days: `Every ${answers.period_interval_days ?? '?'} days`, hire_date: 'Per user hire date' }[answers.period_type || 'none']} />
              <SummaryRow label="Customers" value={answers.customer_site?.customer_csv || 'All customers on site'} />
              <SummaryRow label="Storefront site (ship-to entity)" value={answers.customer_site?.ship_to_csv || '—'} />
              <SummaryRow label="User groups" value={answers.customer_site?.user_groups_csv || '(none)'} />
              <SummaryRow
                label="Program scope type"
                value={
                  answers.customer_site?.restriction_basis === 'delivery'
                    ? 'Shipping-location-level (checkout ship-to addresses)'
                    : 'User-level (enrolled users)'
                }
              />
              <SummaryRow
                label="Eligible users"
                value={
                  answers.customer_site?.restriction_basis === 'delivery'
                    ? '(not used — delivery-based scope)'
                    : answers.customer_site?.user_scope_mode === 'select'
                      ? `${(answers.customer_site?.selected_users || []).length} selected user(s)`
                      : 'All enrolled users under selected customers'
                }
              />
              <SummaryRow
                label="Eligible ship-to addresses"
                value={
                  answers.customer_site?.restriction_basis === 'delivery'
                    ? answers.customer_site?.delivery_scope_mode === 'select'
                      ? `${String(answers.customer_site?.delivery_ship_to_csv || '').split(',').filter(Boolean).length} selected address(es)`
                      : 'All saved addresses for selected customers'
                    : '(not used — user-based scope)'
                }
              />
              <SummaryRow label="Dollar limit / period" value={answers.amount_limit != null ? `$${answers.amount_limit}` : 'No limit'} />
            </div>

            {/* Product list selections */}
            {(answers.product_list_selections || []).length > 0 ? (
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '14px 18px', marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e40af', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <i className="bi bi-boxes" aria-hidden="true" />Applies to {answers.product_list_selections.length} product list{answers.product_list_selections.length !== 1 ? 's' : ''}:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(answers.product_list_selections || []).map(s => (
                    <div key={s.list_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', borderRadius: 8, padding: '8px 14px', border: '1px solid #bfdbfe' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                        <i className="bi bi-check-circle-fill me-1" style={{ color: '#1a56db' }} />
                        {s.list_name}
                      </div>
                      <div style={{ fontSize: 12, color: '#1a56db', fontWeight: 600 }}>
                        {s.max_qty ? `Up to ${s.max_qty} item${s.max_qty !== 1 ? 's' : ''} / period` : 'Unlimited qty'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#92400e', display: 'flex', gap: 6 }}>
                <i className="bi bi-exclamation-triangle-fill" />
                No product lists selected — users will have access to the full catalog.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '16px 24px', borderTop: '1px solid #f3f4f6', background: '#fff',
      }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {stepIdx > 0 && (
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setStepIdx((p) => p - 1)}>
              <i className="bi bi-arrow-left me-1" aria-hidden="true" />Back
            </button>
          )}
          <button type="button" className="btn btn-link btn-sm text-secondary" onClick={onCancel} style={{ textDecoration: 'none' }}>
            Cancel
          </button>
        </div>
        <button
          type="button"
          className={`btn btn-sm ${isSummary ? 'btn-success' : 'btn-primary'}`}
          style={{ fontWeight: 600, minWidth: 120 }}
          onClick={advance}
          disabled={!canAdvance() || saving}
        >
          {saving
            ? <><span className="spinner-border spinner-border-sm me-1" />{Object.keys(initialAnswers).length > 0 ? 'Saving…' : 'Creating…'}</>
            : isSummary
              ? <><i className="bi bi-check-lg me-1" />{Object.keys(initialAnswers).length > 0 ? 'Save Changes' : 'Create Program'}</>
              : <>Next <i className="bi bi-arrow-right ms-1" aria-hidden="true" /></>
          }
        </button>
      </div>

      <style>{`@keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }`}</style>
    </div>
  );
}
